import { useState } from 'react';
import { ProyectoMock } from '../../../../mocks/proyectos';
import { RespuestaAutor } from './types';
import { calcPorcentajeAvance, calcPuntajePromedio, getDecisionColor, getPuntajeBadge, getRondaLabel } from './helpers';

interface Props {
  proyectosConDecision: ProyectoMock[];
  respuestasEnviadas: RespuestaAutor[];
  respuestasPorCodigo: Record<string, RespuestaAutor[]>;
}

export default function TabConDecision({ proyectosConDecision, respuestasEnviadas, respuestasPorCodigo }: Props) {
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [viewingRonda, setViewingRonda] = useState<string>('');

  const toggleExpanded = (codigo: string) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(codigo)) next.delete(codigo);
      else next.add(codigo);
      return next;
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <span className="w-4 h-4 flex items-center justify-center text-teal-600"><i className="ri-award-line" /></span>
            Todos los trabajos evaluados con decisión emitida
          </h3>
          <p className="text-xs text-slate-400 mt-0.5 ml-6">
            Vista del administrador — puntaje de evaluación y rondas de respuesta. Las respuestas se gestionan en <strong>Informe de avance</strong>.
          </p>
        </div>
        <span className="text-xs text-slate-400 bg-slate-100 px-3 py-1 rounded-full">
          {proyectosConDecision.length} proyecto{proyectosConDecision.length !== 1 ? 's' : ''}
        </span>
      </div>

      {proyectosConDecision.length === 0 ? (
        <div className="bg-slate-50 border border-dashed border-slate-200 rounded-lg p-12 text-center text-slate-400">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
            <i className="ri-award-line text-4xl" />
          </div>
          <p className="text-sm font-medium">Aún no hay proyectos con decisión emitida</p>
          <p className="text-xs mt-1">Cuando el Administrador emita decisiones, aparecerán aquí con su puntaje.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {proyectosConDecision.map(p => {
            const respuestasP = respuestasPorCodigo[p.codigo] || [];
            const rondasCompletadas = respuestasP.length;
            const respondido = rondasCompletadas > 0;
            const isExpanded = expandedCards.has(p.codigo);
            const puntaje = calcPuntajePromedio(p);
            const puntajeBadge = getPuntajeBadge(puntaje);
            const decCfg = getDecisionColor(p.decision);
            const { porcentaje: avancePct } = calcPorcentajeAvance(p, respuestasEnviadas);

            return (
              <div key={p.codigo} className={`bg-white border rounded-lg overflow-hidden transition-all ${respondido ? 'border-emerald-300' : 'border-slate-200'}`}>
                {respondido && (
                  <div className="flex items-center gap-3 px-5 py-2.5 bg-emerald-50 border-b border-emerald-200">
                    <span className="w-5 h-5 flex items-center justify-center text-emerald-600 flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-base" />
                    </span>
                    <p className="text-xs font-semibold text-emerald-700 flex-1">
                      {rondasCompletadas === 3
                        ? 'Proceso completado — 3 rondas de respuesta finalizadas'
                        : `El autor ha respondido ${rondasCompletadas} de 3 rondas`}
                      <span className="font-normal text-emerald-600 ml-1">
                        — última: {respuestasP[respuestasP.length - 1]?.fechaEnvio}
                      </span>
                    </p>
                    <button
                      onClick={() => toggleExpanded(p.codigo)}
                      className="whitespace-nowrap cursor-pointer flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-white border border-emerald-200 px-3 py-1.5 rounded-md hover:bg-emerald-100 transition-colors"
                    >
                      <span className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className={isExpanded ? 'ri-eye-off-line' : 'ri-eye-line'} />
                      </span>
                      {isExpanded ? 'Ocultar respuestas' : 'Ver respuestas del autor'}
                    </button>
                  </div>
                )}

                <div className="flex items-start justify-between gap-4 p-5">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className="font-mono text-sm font-bold text-teal-700 bg-teal-50 border border-teal-100 px-2.5 py-0.5 rounded-md">
                        {p.codigo}
                      </span>
                      {puntajeBadge && (
                        <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold flex items-center gap-1 ${puntajeBadge.cls}`}>
                          <span className="w-3.5 h-3.5 flex items-center justify-center"><i className={puntajeBadge.icon} /></span>
                          Evaluación: {puntajeBadge.label}
                        </span>
                      )}
                      <span className={`text-xs px-2.5 py-1 rounded-full border font-medium flex items-center gap-1 ${decCfg.bg}`}>
                        <span className={`w-3.5 h-3.5 flex items-center justify-center ${decCfg.iconColor}`}>
                          <i className={decCfg.icon} />
                        </span>
                        {p.decision}
                      </span>
                      <span className={`text-xs px-2.5 py-1 rounded-full border font-medium flex items-center gap-1 ${
                        rondasCompletadas === 3
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : rondasCompletadas > 0
                          ? 'bg-teal-50 text-teal-700 border-teal-200'
                          : 'bg-slate-50 text-slate-500 border-slate-200'
                      }`}>
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className={rondasCompletadas === 3 ? 'ri-checkbox-circle-fill' : 'ri-refresh-line'} />
                        </span>
                        {rondasCompletadas}/3 rondas
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-slate-800 mb-0.5 truncate">{p.titulo}</p>
                    <p className="text-xs text-slate-500">
                      Investigador/a: <strong>{p.investigador}</strong>
                      {p.fechaDecision && p.fechaDecision !== '—' && (
                        <span className="ml-3 text-slate-400">Decisión: {p.fechaDecision}</span>
                      )}
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                      <span className="text-[10px] text-slate-400 font-medium w-16 flex-shrink-0">Avance:</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-1.5 max-w-32">
                        <div
                          className={`h-1.5 rounded-full transition-all duration-500 ${avancePct >= 80 ? 'bg-emerald-500' : avancePct >= 50 ? 'bg-teal-500' : 'bg-amber-400'}`}
                          style={{ width: `${avancePct}%` }}
                        />
                      </div>
                      <span className="text-xs font-bold text-slate-600">{avancePct}%</span>
                    </div>
                    {p.evaluaciones && p.evaluaciones.length > 0 && (
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide">Evaluadores:</span>
                        {p.evaluaciones.map((ev, idx) => (
                          <span key={idx} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full border border-slate-200">
                            {ev.evaluador} — {ev.recomendacion || 'Sin recomendación'}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    {rondasCompletadas === 3 ? (
                      <div className="flex items-center gap-1.5 text-emerald-600 text-xs font-semibold bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-md">
                        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-checkbox-circle-fill" /></span>
                        Completado
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 text-teal-600 text-xs font-medium bg-teal-50 border border-teal-200 px-3 py-2 rounded-md">
                        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-refresh-line" /></span>
                        {rondasCompletadas === 0 ? 'Sin respuestas aún' : `${3 - rondasCompletadas} ronda${3 - rondasCompletadas > 1 ? 's' : ''} restante${3 - rondasCompletadas > 1 ? 's' : ''}`}
                      </div>
                    )}
                  </div>
                </div>

                {respondido && isExpanded && (
                  <div className="border-t border-emerald-100 bg-slate-50 px-5 py-4">
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <span className="w-4 h-4 flex items-center justify-center text-emerald-600"><i className="ri-file-text-line" /></span>
                      Historial de respuestas — {respuestasP[0]?.investigador}
                    </p>
                    <div className="space-y-3">
                      {respuestasP.map(resp => {
                        const key = `${resp.codigo}-${resp.ronda}`;
                        const isViewing = viewingRonda === key;
                        return (
                          <div key={resp.id} className={`border rounded-lg overflow-hidden ${resp.dentroDelPlazo ? 'border-emerald-200' : 'border-amber-200'}`}>
                            <div className={`flex items-center justify-between gap-3 px-4 py-2.5 ${resp.dentroDelPlazo ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                              <div className="flex items-center gap-3">
                                <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full border ${resp.dentroDelPlazo ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : 'bg-amber-100 text-amber-700 border-amber-300'}`}>
                                  {getRondaLabel(resp.ronda)}
                                </span>
                                <span className="text-[10px] text-slate-400">{resp.fechaEnvio}</span>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${resp.dentroDelPlazo ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {resp.dentroDelPlazo ? 'En plazo (+20%)' : 'Fuera de plazo (+10%)'}
                                </span>
                              </div>
                              <button
                                onClick={() => setViewingRonda(isViewing ? '' : key)}
                                className={`whitespace-nowrap cursor-pointer flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border transition-colors ${resp.dentroDelPlazo ? 'text-emerald-700 bg-white border-emerald-200 hover:bg-emerald-100' : 'text-amber-700 bg-white border-amber-200 hover:bg-amber-100'}`}
                              >
                                <span className="w-3.5 h-3.5 flex items-center justify-center">
                                  <i className={isViewing ? 'ri-eye-off-line' : 'ri-eye-line'} />
                                </span>
                                {isViewing ? 'Ocultar' : 'Ver'}
                              </button>
                            </div>
                            {isViewing && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-white">
                                {resp.actividadesRealizadas && (
                                  <div className="bg-slate-50 rounded-md p-3 border border-slate-100">
                                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Actividades realizadas</p>
                                    <p className="text-xs text-slate-700 leading-relaxed">{resp.actividadesRealizadas}</p>
                                  </div>
                                )}
                                {resp.resultadosObtenidos && (
                                  <div className="bg-slate-50 rounded-md p-3 border border-slate-100">
                                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Resultados obtenidos</p>
                                    <p className="text-xs text-slate-700 leading-relaxed">{resp.resultadosObtenidos}</p>
                                  </div>
                                )}
                                {resp.dificultades && (
                                  <div className="bg-amber-50 rounded-md p-3 border border-amber-100">
                                    <p className="text-[10px] font-semibold text-amber-500 uppercase tracking-wide mb-1.5">Dificultades</p>
                                    <p className="text-xs text-slate-700 leading-relaxed">{resp.dificultades}</p>
                                  </div>
                                )}
                                {resp.observacionesAdicionales && (
                                  <div className="bg-white rounded-md p-3 border border-slate-200">
                                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Observaciones adicionales</p>
                                    <p className="text-xs text-slate-700 leading-relaxed">{resp.observacionesAdicionales}</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
